import type { MaskRuleConfig } from "../contracts/types";

/**
 * Persistencia local opcional (opt-in) en IndexedDB para las reglas de
 * máscara elegidas por el visitante (06-modelo-datos.md: "por defecto la
 * sesión vive en memoria"). Solo se usa si el visitante activa "Guardar
 * reglas en este navegador". Nunca se envía nada a un servidor: es
 * exclusivamente `window.indexedDB`, y nunca guarda el dataset en sí, solo
 * la configuración de reglas (campo + estrategia), para minimizar lo que
 * queda persistido localmente.
 */
const DB_NAME = "data-anonymizer";
const DB_VERSION = 1;
const STORE_NAME = "rules";

export interface StoredRules {
  scenarioId: string;
  updatedAt: string;
  rules: MaskRuleConfig[];
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB no está disponible en este navegador."));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "scenarioId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("No se pudo abrir IndexedDB."));
  });
}

export function isLocalStorageSupported(): boolean {
  return typeof indexedDB !== "undefined";
}

export async function saveRules(record: StoredRules): Promise<void> {
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("No se pudo guardar en IndexedDB."));
  });
  db.close();
}

export async function loadRules(scenarioId: string): Promise<StoredRules | undefined> {
  const db = await openDatabase();
  const record = await new Promise<StoredRules | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(scenarioId);
    request.onsuccess = () => resolve(request.result as StoredRules | undefined);
    request.onerror = () => reject(request.error ?? new Error("No se pudo leer IndexedDB."));
  });
  db.close();
  return record;
}

/** Botón "Eliminar datos locales": borra toda la base de datos del origen. */
export async function clearLocalData(): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error("No se pudo eliminar IndexedDB."));
    request.onblocked = () => resolve();
  });
}
